import React, { useMemo } from "react";
import { withTranslation } from "react-i18next";

import { ThemeProvider } from "@mui/material/styles";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import "antd/dist/antd.css";
import moment from "moment-timezone";
import TimePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useController, useWatch, useForm } from "react-hook-form";

import "@css/components/Modals/mobileTimePicker.css";

import {
  getFormatedValueHoursMin,
  isValidHourFormat,
  onTimepickerRef,
  timePickerTheme
} from "@helpers/_functions";

const ArhTimePicker = ({
  selectedTime,
  handleTimeChange,
  svgIcones,
  label,
  invalid = false,
  classLabel = "",
  control: mainControl = null,
  textName = "timeTextName",
  timeIntervals,
  allowEmptyValue,
  toRound = false,
  disabled = false
}) => {
  const { control: localControl } = useForm();

  const control = useMemo(() => {
    return mainControl || localControl;
  }, [mainControl, localControl]);

  const {
    field: timeTextField,
  } = useController({
    name: textName,
    control,
    defaultValue: Boolean(selectedTime)
      ? moment(selectedTime).format("HH:mm")
      : ""
  });

  const timeText = useWatch({ control, name: textName });

  const handleChangeRaw = (e) => {
    e.preventDefault();
    const newTime = getFormatedValueHoursMin(e.target?.value);
    timeTextField.onChange(newTime);
  };

  const handleBlur = (newTime) => {
    const format = "HH:mm";
    if (newTime && isValidHourFormat(newTime)) {
      if (toRound) {
        const hours = newTime.split(":")[0];
        const minutes = Math.round(Number(newTime.split(":")[1]) / timeIntervals) * timeIntervals;
        newTime = `${hours}:${minutes.toString().padStart(2, "0")}`;
        timeTextField.onChange(newTime);
      }
      let newDate = moment(newTime, format).toDate();
      if (selectedTime) {
        const [heures, minutes] = newTime.split(':').map(Number);
        newDate = moment(selectedTime).set({ hour: heures, minute: minutes, second: 0, millisecond: 0 }).toDate();
      }
      handleTimeChange(newDate);
    } else {
      if (allowEmptyValue && !newTime && handleTimeChange) {
        handleTimeChange(null);
      } else {
        const prevStartTimeText = selectedTime
          ? moment(selectedTime).format(format)
          : "";
        timeTextField.onChange(prevStartTimeText);
      }
    }
  };

  const handleChange = (date) => {
    timeTextField.onChange(moment(date).format("HH:mm"));
    handleTimeChange(date);
  };

  return (
    <div className={`new-timepicker ${invalid ? "invalid" : ""}`}>
      {label && (
        <label
          className={`
            arh-mb-1 arh-w-full arh-text-left arh-font-poppins arh-text-xs arh-font-medium
            ${classLabel}
            ${invalid ? "arh-text-[#F60A20]" : disabled ? "arh-text-greyDisabledText" : "arh-text-black"}
          `}
        >
          {label}
        </label>
      )}
      <style>
        {`
        .react-datepicker-popper.popperResumeHour {
          width: fit-content;
        }
        .react-datepicker__header.react-datepicker__header--time.react-datepicker__header--time--only {
          display: none;
        }
      `}
      </style>
      <div className="arh-relative arh-bg-white">
        <ThemeProvider theme={timePickerTheme}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <TimePicker
              ref={(ref) => onTimepickerRef(ref, handleBlur)}
              selected={selectedTime}
              className={`focus:arh-shadow-blue arh-w-full arh-rounded arh-border arh-border-solid arh-px-6 arh-py-3 arh-text-xs
                ${invalid ? "arh-text-[#F60A20] arh-border-invalidColorTextInput" :
                  disabled ? "arh-text-greyDisabledText arh-border-borderTable arh-pointer-events-none" :
                    "arh-text-darkGreyColor arh-border-greyBorderInput"} focus:arh-border-[#C2D8F9] focus:arh-bg-[#F3F8FFB2] arh-font-poppins arh-font-medium arh-text-center arh-bg-white`}
              value={disabled ? "--" : timeText}
              onChange={handleChange}
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={timeIntervals}
              dateFormat="HH:mm"
              timeFormat="HH:mm"
              popperClassName="popperResumeHour"
              onChangeRaw={handleChangeRaw}
              popperPlacement="bottom-start"
            />
            {Boolean(svgIcones) && (
              <div className="arh-pointer-events-none arh-absolute arh-right-2 arh-top-1/2 -arh-translate-y-1/2">
                {svgIcones()}
              </div>
            )}
          </LocalizationProvider>
        </ThemeProvider>
      </div>
    </div>
  );
};

export default withTranslation()(ArhTimePicker);
